import React, { useState } from 'react';
import { Tooltip, Dropdown, Menu } from 'antd';
import { MoreOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import {
  Eye,
  MousePointerClick,
  ShoppingCart,
  DollarSign,
  TrendingUp,
  Percent,
  UserPlus,
  Clock,
} from 'lucide-react';

import {
  formatCurrency2SigFig,
  formatWholeNumberNaN,
  computeROASDisplay,
} from '../../../../../../../core/utils/campaigns';

import { roasFormat } from '../../../../../../../core/utils/constants/constants';
import { KpiCard } from '../../../../../../shared/designSystem/components/KpiBar';

// Fully migrated to shared `KpiCard`. Summary tab's local KpiCard was removed
// after the `.campaign-summary__kpi-*` BEM scope's rich styles (head-left
// wrapper, more button, help icon, valueadd + context slots) were promoted
// into the `.campaign-overview__kpi-*` scope in `_overview.scss`. Shared
// KpiCard gained a `footer` prop + auto-wraps its head in `__kpi-head-left`
// when a `trailing` slot is passed, covering every Summary feature.
//
// Consumer-side renames: `.campaign-summary__kpi-more` → `.campaign-overview
// __kpi-more`, same for `__kpi-help` / `__kpi-valueadd` / `__kpi-context`.
// `delta` prop consolidated from separate `deltaValue/deltaPrev/deltaRanges`
// props to a single object `{ value, prev, ranges }`.

// =============================================================================
// Main component — renders the KPI bar with conditional columns.
// ALL props + conditional logic preserved from prior version.
// =============================================================================
const DetailBarDynamicCol = ({
  clicks = 0,
  impressions = 0,
  transactions = 0,
  totalWalkIns = 0,
  transactionTotalRevenue = 0,
  showTransactions,
  showAttribution,
  hasEcommerceAttribution,
  signUpCampaign,
  signUpsData,
  summaryData,
  bySummaryRangeDate,
  budget = 0,
  signUps = 0,
  selectedCampaign,
  priceVisible,
  kpiPercentage = {
    percentages: {},
    previousValues: {},
    ranges: '',
  },
  dataConfig,
  orgCurrencyFormat,
  orgRoasFormat,
  spend = 0,
  sessions = 0,
  showSessions,
}) => {
  const [roasFormatSelected, setRoasFormatSelected] = useState(
    orgRoasFormat && orgRoasFormat ? orgRoasFormat.key : null
  );
  const mediaSpend = spend;
  const { percentages, previousValues, ranges } = kpiPercentage;
  const lineItems = selectedCampaign?.campaigns?.[0]?.lineItems || [];
  const lineItemsValueAdd = lineItems.map((item) => item);
  const lineItemsValueAddBudget = lineItemsValueAdd.map((item) =>
    item.valueAdd ? item.budget : null
  );
  const spendValueAddTotal = lineItemsValueAddBudget.reduce(
    (acc, curr) => acc + (curr || null),
    null
  );

  return (
    <div className="campaign-overview__kpi-bar">
      <KpiCard
        icon={<Eye size={16} />}
        iconTint="primary"
        label="Impressions"
        value={formatWholeNumberNaN(impressions)}
        delta={{
          value: percentages.impressions,
          prev: formatWholeNumberNaN(previousValues.impressions),
          ranges,
        }}
      />

      <KpiCard
        icon={<MousePointerClick size={16} />}
        iconTint="emerald"
        label="Clicks"
        value={formatWholeNumberNaN(clicks)}
        delta={{
          value: percentages.clicks,
          prev: formatWholeNumberNaN(previousValues.clicks),
          ranges,
        }}
      />

      {showTransactions && (
        <KpiCard
          icon={<ShoppingCart size={16} />}
          iconTint="amber"
          label="Transactions"
          labelTooltip="Multi-Touch Attribution (MTA). Using device-level data, this MTA model shows impressions have been served to device identifiers that have also made summary. This model does not take into account other factors that may have influenced the purchase such as search, newsletters or other marketing techniques. MTA should also not be confused with other models. ie: Last-Interaction or Position-based"
          value={formatWholeNumberNaN(transactions)}
          delta={{
            value: percentages.transactions,
            prev: formatWholeNumberNaN(previousValues.transactions),
            ranges,
          }}
        />
      )}

      {priceVisible && (
        <KpiCard
          icon={<DollarSign size={16} />}
          iconTint="indigo"
          label="Spend"
          value={formatCurrency2SigFig(mediaSpend, orgCurrencyFormat)}
          delta={{
            value: percentages.spend,
            prev: formatCurrency2SigFig(previousValues.spend, orgCurrencyFormat),
            ranges,
          }}
          footer={
            spendValueAddTotal ? (
              <div className="campaign-overview__kpi-valueadd">
                {formatCurrency2SigFig(spendValueAddTotal, orgCurrencyFormat)} saved with Value Add
              </div>
            ) : null
          }
        />
      )}

      {showTransactions && hasEcommerceAttribution && (
        <KpiCard
          icon={<TrendingUp size={16} />}
          iconTint="emerald"
          label="Revenue"
          value={
            !isNaN(parseFloat(transactionTotalRevenue))
              ? formatCurrency2SigFig(parseFloat(transactionTotalRevenue), orgCurrencyFormat)
              : ''
          }
          delta={{
            value: percentages.revenue,
            prev: formatCurrency2SigFig(previousValues.revenue, orgCurrencyFormat),
            ranges,
          }}
        />
      )}

      {priceVisible && hasEcommerceAttribution && (
        <KpiCard
          icon={<Percent size={16} />}
          iconTint="purple"
          label="ROAS"
          value={computeROASDisplay(
            transactionTotalRevenue,
            mediaSpend,
            roasFormatSelected,
            orgCurrencyFormat
          )}
          delta={{
            value: percentages.roas,
            prev: computeROASDisplay(
              previousValues.revenue,
              previousValues.spend,
              roasFormatSelected,
              orgCurrencyFormat
            ),
            ranges,
          }}
          trailing={
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Dropdown
                placement="bottomRight"
                overlayClassName="mjl-ds"
                overlay={
                  <Menu>
                    {roasFormat.map((d) => (
                      <Menu.Item
                        key={d.key}
                        onClick={() => setRoasFormatSelected(d.key)}
                      >
                        {d.name}
                      </Menu.Item>
                    ))}
                  </Menu>
                }
              >
                <button
                  type="button"
                  className="campaign-overview__kpi-more"
                  aria-label="ROAS format options"
                >
                  <MoreOutlined />
                </button>
              </Dropdown>
              <Tooltip
                overlayClassName="mjl-ds"
                title="ROAS Format can be changed by pressing the more icon besides this tooltip. Select the desired format of the ROAS on the dropdown. If the account is admin it can be changed in the org config"
                arrowPointAtCenter={true}
                placement="bottomRight"
              >
                <QuestionCircleOutlined className="campaign-overview__kpi-help" />
              </Tooltip>
            </div>
          }
        />
      )}

      {signUpCampaign && (
        <KpiCard
          icon={<UserPlus size={16} />}
          iconTint="indigo"
          label="Sign Ups"
          value={formatWholeNumberNaN(signUps)}
          delta={{
            value: percentages.signUps,
            prev: formatWholeNumberNaN(previousValues.signUps),
            ranges,
          }}
        />
      )}

      {showSessions && (
        <KpiCard
          icon={<Clock size={16} />}
          iconTint="neutral"
          label="Sessions"
          value={formatWholeNumberNaN(sessions)}
          delta={{
            value: percentages.sessions,
            prev: formatWholeNumberNaN(previousValues.sessions),
            ranges,
          }}
        />
      )}
    </div>
  );
};

export default DetailBarDynamicCol;
